import React, { useCallback, useRef } from "react";
import { PROFESSIONS, PROFESSIONS_STRIP } from "../../data/startPageContent";
import useStartReveal from "./useStartReveal";
import "./StartProfessions.css";

/**
 * Profession chips (plan §4.2). Accessible radiogroup; selection is lifted to
 * StartPage which also syncs `?p=` in the URL.
 */
export default function StartProfessions({ value, onChange }) {
  const ref = useStartReveal();
  const groupRef = useRef(null);

  const select = useCallback(
    (key) => {
      onChange?.(key === value ? null : key);
    },
    [onChange, value]
  );

  // Arrow-key navigation inside the radiogroup (roving tabindex).
  const onKeyDown = (e, idx) => {
    const n = PROFESSIONS.length;
    let next = null;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = (idx + 1) % n; // RTL: left = forward
    else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = (idx - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    if (next === null) return;
    e.preventDefault();
    const btn = groupRef.current?.querySelectorAll("[role=radio]")[next];
    btn?.focus();
    onChange?.(PROFESSIONS[next].key);
  };

  const activeIdx = Math.max(0, PROFESSIONS.findIndex((p) => p.key === value));

  return (
    <section id="start-professions" className="st-section st-section--card st-pros" aria-labelledby="st-pros-title">
      <div className="st-container st-reveal" ref={ref}>
        <p className="st-overline">{PROFESSIONS_STRIP.overline}</p>
        <h2 id="st-pros-title" className="st-sr-only">{PROFESSIONS_STRIP.groupLabel}</h2>
        <div className="st-pros-chips" role="radiogroup" aria-labelledby="st-pros-title" ref={groupRef}>
          {PROFESSIONS.map((p, idx) => {
            const selected = p.key === value;
            return (
              <button
                key={p.key}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={idx === activeIdx ? 0 : -1}
                className={`st-chip${selected ? " st-chip--selected" : ""}`}
                onClick={() => select(p.key)}
                onKeyDown={(e) => onKeyDown(e, idx)}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <p className="st-pros-fallback">{PROFESSIONS_STRIP.fallback}</p>
      </div>
    </section>
  );
}
